import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Clock, FlaskConical, MailCheck, MailX, RefreshCw, Send } from 'lucide-react';
import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { isApiError } from '../../../api/client';
import { salesApi } from '../../../api/endpoints';
import { queryKeys } from '../../../api/queries';
import type { DeliveryView, SalesDocumentDetail } from '../../../api/types';
import { useAccess } from '../../../app/access';
import { useI18n } from '../../../i18n/I18nProvider';
import { fillTemplate } from '../../../lib/fillTemplate';
import { Button } from '../../../ui/Button';
import { useToast } from '../../../ui/Toast';
import styles from '../Sales.module.css';

/**
 * The delivery states the page shows (BLOCK-1B-EMAIL-DESIGN §4.2, AC-M8). The business status `sent`
 * is «سلّمناها لخادم البريد» (SMTP) or «حُفظت كرسالة تجريبية» (the file transport); a copy ETA's
 * refusal held back is `withheld`. There is no `viewed`: nothing can report that a message was read.
 */
export const DELIVERY_KEYS = ['queued', 'sending', 'sent', 'saved', 'failed', 'withheld'] as const;
export type DeliveryKey = (typeof DELIVERY_KEYS)[number];

export function deliveryKey(d: DeliveryView): DeliveryKey {
  if (d.status === 'sent') return d.transport === 'file' ? 'saved' : 'sent';
  if (d.status === 'failed') return d.error?.code === 'eta_not_valid' ? 'withheld' : 'failed';
  return d.status;
}

const CHIP: Record<DeliveryKey, { tone: string; icon: ReactNode }> = {
  queued: { tone: 'neutral', icon: <Clock size={14} aria-hidden="true" /> },
  sending: { tone: 'info', icon: <Send size={14} aria-hidden="true" /> },
  sent: { tone: 'success', icon: <MailCheck size={14} aria-hidden="true" /> },
  saved: { tone: 'lab', icon: <FlaskConical size={14} aria-hidden="true" /> },
  failed: { tone: 'danger', icon: <MailX size={14} aria-hidden="true" /> },
  withheld: { tone: 'warning', icon: <MailX size={14} aria-hidden="true" /> },
};

/**
 * «نسخة المشتري بالبريد» (WP-1b.4, MF-INV-010; design §4): on every issued document, after the Tax
 * Authority's verdict and before the at-a-glance tiles. One status line (a chip with text and an
 * icon, never colour alone) and its details; «أعِد محاولة الإرسال» sits with the failure it answers,
 * only after a transport failure and only with `invoice.issue` (hidden, never disabled). The stage
 * badge never shows a delivery value (the two axes, DEC-041).
 */
export function DeliverySection({ orgId, detail }: { orgId: string; detail: SalesDocumentDetail }) {
  const { t, formatDateTime } = useI18n();
  const access = useAccess(orgId);
  const toast = useToast();
  const queryClient = useQueryClient();
  const titleId = useId();
  const d = detail.delivery;
  const v = t.detail.delivery;
  const key = d ? deliveryKey(d) : null;

  // A change of state is announced once, politely; never on the first render (AC-M13).
  const [announcement, setAnnouncement] = useState('');
  const seen = useRef(key);
  useEffect(() => {
    if (seen.current === key) return;
    seen.current = key;
    if (key) setAnnouncement(`${v.title}: ${v.status[key]}`);
  }, [key, v]);

  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: [...queryKeys.org(orgId), 'sales'] });
  const retry = useMutation({
    mutationFn: () => salesApi.retryEmail(orgId, detail.document.id),
    onSuccess: async () => {
      toast.success(v.retried);
      await refresh();
    },
    onError: async (error) => {
      // 409 email_not_failed: the state moved on; show the latest one.
      toast.error(error);
      if (isApiError(error)) await refresh();
    },
  });

  const status = detail.document.status;
  if (status === 'draft' || status === 'discarded') return null;
  const chip = key ? CHIP[key] : null;
  const time = (value: string | null) => (value ? formatDateTime(value) : '—');
  return (
    <section
      className={styles.card}
      aria-labelledby={titleId}
      data-testid="delivery"
      data-status={d?.status ?? 'none'}
      {...(d ? { 'data-transport': d.transport } : {})}
    >
      <div className={styles.cardHead}>
        <h2 id={titleId} className={styles.cardTitle}>
          {v.title}
        </h2>
      </div>
      <div className={`${styles.cardBody} ${styles.delivery}`}>
        {!d || !key || !chip ? (
          <p className={styles.subtle} data-testid="delivery-none">
            {v.none}
          </p>
        ) : (
          <>
            <div className={styles.deliveryLine}>
              <span
                className={styles.deliveryChip}
                data-tone={chip.tone}
                data-testid="delivery-status"
                data-status={d.status}
                data-key={key}
                {...(d.error ? { 'data-code': d.error.code } : {})}
              >
                {chip.icon}
                <span>{v.status[key]}</span>
              </span>
              {key !== 'withheld' ? (
                <span>
                  <span className={styles.subtle}>{v.recipient} </span>
                  <bdi dir="ltr" className={styles.mailAddress} data-testid="delivery-recipient">
                    {d.recipient}
                  </bdi>
                </span>
              ) : null}
            </div>
            {key === 'queued' && d.waitingForEta ? (
              <p className={styles.subtle} data-testid="delivery-waiting">
                {v.waitingEta}
              </p>
            ) : null}
            {key === 'sent' || key === 'saved' ? (
              // RIM-01: «سُلّمت في» only when a mail server took it; a test message was saved.
              <p className={styles.subtle} data-testid="delivery-time">
                {key === 'sent' ? v.sentAt(time(d.sentAt)) : v.savedAt(time(d.sentAt))}
              </p>
            ) : null}
            {key === 'saved' ? (
              <p className={styles.mailTestNote} data-testid="delivery-test-note">
                <FlaskConical size={14} aria-hidden="true" />{' '}
                <span>
                  {fillTemplate(
                    v.testNote,
                    <bdi dir="ltr" className={styles.mono}>
                      .eml
                    </bdi>,
                  )}
                </span>
              </p>
            ) : null}
            {key === 'failed' ? (
              <>
                <ul className={styles.deliveryFacts}>
                  <li data-testid="delivery-attempts">{v.attempts(String(d.attempts))}</li>
                  <li data-testid="delivery-last">{v.lastAttempt(time(d.lastAttemptAt))}</li>
                  {d.nextAttemptAt ? (
                    <li data-testid="delivery-next">{v.nextAttempt(time(d.nextAttemptAt))}</li>
                  ) : null}
                </ul>
                {d.error ? (
                  <dl className={styles.deliveryReply}>
                    <dt>{v.reply}</dt>
                    <dd>
                      <code
                        dir="ltr"
                        lang="en"
                        className={styles.mailAddress}
                        data-testid="delivery-error"
                        data-code={d.error.code}
                      >
                        {d.error.message ?? d.error.code}
                      </code>
                    </dd>
                  </dl>
                ) : null}
                {access.can('invoice.issue') ? (
                  <div>
                    <Button
                      variant="secondary"
                      icon={<RefreshCw size={16} aria-hidden="true" />}
                      isPending={retry.isPending}
                      onPress={() => retry.mutate()}
                      testId="delivery-retry"
                    >
                      {v.retry}
                    </Button>
                  </div>
                ) : null}
              </>
            ) : null}
          </>
        )}
        <p className="visually-hidden" role="status" data-testid="delivery-announcer">
          {announcement}
        </p>
      </div>
    </section>
  );
}
